import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  BuildingOfficeIcon,
  CurrencyRupeeIcon,
  ChartBarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  CubeIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { cleanDataForUpdate } from '../../utils/data-cleanup';

interface CostCenter {
  id: string;
  code: string;
  name: string;
  type: string;
  plantId: string;
  plant?: {
    id: string;
    name: string;
    code: string;
  };
  parent?: CostCenter;
  children?: CostCenter[];
  description?: string;
  managerName?: string;
  managerEmail?: string;
  annualBudget: number;
  monthlyBudget: number;
  allocationKeys?: Record<string, number>;
  isDirectCost: boolean;
  isProfitCenter: boolean;
  glAccountCode?: string;
  analyticalAccountCode?: string;
  workCenters?: any[];
  isActive: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

interface Plant {
  id: string;
  code: string;
  name: string;
}

const CostCenterType = {
  PRODUCTION: 'production',
  SERVICE: 'service',
  SUPPORT: 'support',
  ADMINISTRATION: 'administration',
  SALES: 'sales',
  MAINTENANCE: 'maintenance',
};

const CostCenterManagement: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCostCenter, setSelectedCostCenter] = useState<CostCenter | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPlant, setFilterPlant] = useState('');
  const [filterType, setFilterType] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'tree'>('grid');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  
  const queryClient = useQueryClient();

  // Fetch cost centers
  const { data: costCentersData, isLoading } = useQuery({
    queryKey: ['costCenters', searchTerm, filterPlant, filterType],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterPlant) params.append('plantId', filterPlant);
      if (filterType) params.append('type', filterType);
      
      const response = await api.get(`/master-data/cost-centers?${params}`);
      return response.data;
    },
  });

  // Fetch cost centers tree
  const { data: treeData } = useQuery({
    queryKey: ['costCentersTree'],
    queryFn: async () => {
      const response = await api.get('/master-data/cost-centers/tree');
      return response.data;
    },
    enabled: viewMode === 'tree',
  });

  // Fetch plants for dropdown
  const { data: plantsData } = useQuery({
    queryKey: ['plants'],
    queryFn: async () => {
      const response = await api.get('/master-data/plants');
      return response.data;
    },
  });

  // Fetch all cost centers for parent dropdown
  const { data: allCostCenters } = useQuery({
    queryKey: ['allCostCenters'],
    queryFn: async () => {
      const response = await api.get('/master-data/cost-centers?limit=100');
      return response.data;
    },
  });

  // Create/Update cost center
  const createUpdateMutation = useMutation({
    mutationFn: async (data: any) => {
      if (selectedCostCenter) {
        // For updates, remove plantId as it cannot be changed
        const cleanData = cleanDataForUpdate(data, ['plantId']);
        return api.patch(`/master-data/cost-centers/${selectedCostCenter.id}`, cleanData);
      }
      // For create, just clean the data normally
      const cleanData = cleanDataForUpdate(data, []);
      return api.post('/master-data/cost-centers', cleanData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['costCenters'] });
      queryClient.invalidateQueries({ queryKey: ['costCentersTree'] });
      toast.success(selectedCostCenter ? 'Cost center updated successfully' : 'Cost center created successfully');
      closeModal();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Operation failed');
    },
  });

  // Delete cost center
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.delete(`/master-data/cost-centers/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['costCenters'] });
      queryClient.invalidateQueries({ queryKey: ['costCentersTree'] });
      toast.success('Cost center deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Delete failed');
    },
  });

  // Toggle status
  const toggleStatusMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.patch(`/master-data/cost-centers/${id}/toggle-status`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['costCenters'] });
      queryClient.invalidateQueries({ queryKey: ['costCentersTree'] });
      toast.success('Status updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Status update failed');
    },
  });

  // Get budget summary
  const { data: budgetSummary, refetch: refetchBudgetSummary } = useQuery({
    queryKey: ['budgetSummary', selectedCostCenter?.id],
    queryFn: async () => {
      if (!selectedCostCenter?.id) return null;
      const response = await api.get(`/master-data/cost-centers/${selectedCostCenter.id}/budget-summary`);
      return response.data;
    },
    enabled: false,
  });

  const openModal = (costCenter?: CostCenter) => {
    setSelectedCostCenter(costCenter || null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedCostCenter(null);
  };

  const toggleExpand = (id: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedNodes(newExpanded);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case CostCenterType.PRODUCTION:
        return <CubeIcon className="h-5 w-5" />;
      case CostCenterType.ADMINISTRATION:
        return <BuildingOfficeIcon className="h-5 w-5" />;
      case CostCenterType.SALES:
        return <CurrencyRupeeIcon className="h-5 w-5" />;
      default:
        return <ChartBarIcon className="h-5 w-5" />;
    }
  };

  const getTypeColor = (type: string) => {
    const typeColors = {
      [CostCenterType.PRODUCTION]: 'bg-blue-100 text-blue-800',
      [CostCenterType.SERVICE]: 'bg-purple-100 text-purple-800',
      [CostCenterType.SUPPORT]: 'bg-gray-100 text-gray-800',
      [CostCenterType.ADMINISTRATION]: 'bg-orange-100 text-orange-800',
      [CostCenterType.SALES]: 'bg-green-100 text-green-800',
      [CostCenterType.MAINTENANCE]: 'bg-yellow-100 text-yellow-800',
    };

    return typeColors[type] || 'bg-gray-100 text-gray-800';
  };

  const renderTreeNode = (node: CostCenter, level: number = 0) => {
    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = node.children && node.children.length > 0;

    return (
      <div key={node.id} className="mb-2">
        <div
          className={`flex items-center gap-2 p-3 rounded-lg hover:bg-gray-50 ${
            level > 0 ? `ml-${level * 8}` : ''
          }`}
          style={{ marginLeft: `${level * 32}px` }}
        >
          {hasChildren && (
            <button
              onClick={() => toggleExpand(node.id)}
              className="p-1 hover:bg-gray-200 rounded"
            >
              {isExpanded ? (
                <ChevronDownIcon className="h-4 w-4" />
              ) : (
                <ChevronRightIcon className="h-4 w-4" />
              )}
            </button>
          )}
          {!hasChildren && <div className="w-6" />}

          <div className="flex-1 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {getTypeIcon(node.type)}
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{node.name}</span>
                  <span className="text-sm text-gray-500">({node.code})</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${getTypeColor(node.type)}`}>
                    {node.type}
                  </span>
                </div>
                <div className="text-sm text-gray-600">
                  Budget: ₹{node.annualBudget.toLocaleString()} / year
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => toggleStatusMutation.mutate(node.id)}
                className={`flex items-center gap-1 px-2 py-1 rounded text-sm ${
                  node.isActive
                    ? 'text-green-600 hover:bg-green-50'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {node.isActive ? (
                  <CheckCircleIcon className="h-4 w-4" />
                ) : (
                  <XCircleIcon className="h-4 w-4" />
                )}
              </button>
              <button
                onClick={() => openModal(node)}
                title="Edit"
                aria-label="Edit"
                className="p-1 hover:bg-gray-100 rounded"
              >
                <PencilIcon className="h-4 w-4 text-gray-600" />
              </button>
              <button
                onClick={() => {
                  if (window.confirm('Are you sure you want to delete this cost center?')) {
                    deleteMutation.mutate(node.id);
                  }
                }}
                title="Delete"
                aria-label="Delete"
                className="p-1 hover:bg-red-50 rounded"
              >
                <TrashIcon className="h-4 w-4 text-red-600" />
              </button>
            </div>
          </div>
        </div>

        {hasChildren && isExpanded && (
          <div>
            {node.children!.map((child) => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Cost Center Management</h1>
        <p className="text-gray-600">Manage cost centers and budget allocations</p>
      </div>

      {/* Filters and Actions */}
      <Card className="mb-6">
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search cost centers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <select
              value={filterPlant}
              onChange={(e) => setFilterPlant(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Plants</option>
              {plantsData?.data?.map((plant: Plant) => (
                <option key={plant.id} value={plant.id}>
                  {plant.name}
                </option>
              ))}
            </select>

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Types</option>
              {Object.entries(CostCenterType).map(([key, value]) => (
                <option key={value} value={value}>
                  {key.charAt(0) + key.slice(1).toLowerCase()}
                </option>
              ))}
            </select>

            <div className="flex gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-4 py-2 rounded-lg ${
                  viewMode === 'grid'
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-200 text-gray-700'
                }`}
              >
                Grid
              </button>
              <button
                onClick={() => setViewMode('tree')}
                className={`px-4 py-2 rounded-lg ${
                  viewMode === 'tree'
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-200 text-gray-700'
                }`}
              >
                Tree
              </button>
            </div>

            <Button onClick={() => openModal()} className="flex items-center gap-2">
              <PlusIcon className="h-5 w-5" />
              Add Cost Center
            </Button>
          </div>
        </div>
      </Card>

      {/* Content based on view mode */}
      {isLoading ? (
        <div className="text-center py-8">Loading...</div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {costCentersData?.data?.map((costCenter: CostCenter) => (
            <Card key={costCenter.id} className="hover:shadow-lg transition-shadow">
              <div className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {getTypeIcon(costCenter.type)}
                    <div>
                      <h3 className="font-semibold text-gray-900">{costCenter.name}</h3>
                      <p className="text-sm text-gray-500">{costCenter.code}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(costCenter.type)}`}>
                    {costCenter.type}
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Plant:</span>
                    <span className="font-medium">{costCenter.plant?.name}</span>
                  </div>
                  {costCenter.parent && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Parent:</span>
                      <span className="font-medium">{costCenter.parent.name}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Annual Budget:</span>
                    <span className="font-medium">₹{costCenter.annualBudget.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Monthly Budget:</span>
                    <span className="font-medium">₹{costCenter.monthlyBudget.toLocaleString()}</span>
                  </div>
                  {costCenter.managerName && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Manager:</span>
                      <span className="font-medium">{costCenter.managerName}</span>
                    </div>
                  )}
                  <div className="flex gap-2 mt-2">
                    {costCenter.isDirectCost && (
                      <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded">
                        Direct Cost
                      </span>
                    )}
                    {costCenter.isProfitCenter && (
                      <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded">
                        Profit Center
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-3 border-t">
                  <button
                    onClick={() => toggleStatusMutation.mutate(costCenter.id)}
                    className={`flex items-center gap-1 px-2 py-1 rounded text-sm ${
                      costCenter.isActive
                        ? 'text-green-600 hover:bg-green-50'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {costCenter.isActive ? (
                      <CheckCircleIcon className="h-4 w-4" />
                    ) : (
                      <XCircleIcon className="h-4 w-4" />
                    )}
                    {costCenter.isActive ? 'Active' : 'Inactive'}
                  </button>

                  <div className="flex gap-2">
                    <button
                      onClick={() => openModal(costCenter)}
                      title="Edit"
                      aria-label="Edit"
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <PencilIcon className="h-4 w-4 text-gray-600" />
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm('Are you sure you want to delete this cost center?')) {
                          deleteMutation.mutate(costCenter.id);
                        }
                      }}
                      title="Delete"
                      aria-label="Delete"
                      className="p-1 hover:bg-red-50 rounded"
                    >
                      <TrashIcon className="h-4 w-4 text-red-600" />
                    </button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <div className="p-4">
            {treeData?.map((node: CostCenter) => renderTreeNode(node))}
          </div>
        </Card>
      )}

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <CostCenterModal
          costCenter={selectedCostCenter}
          plants={plantsData?.data || []}
          costCenters={allCostCenters?.data || []}
          onClose={closeModal}
          onSubmit={(data) => createUpdateMutation.mutate(data)}
        />
      )}
    </div>
  );
};

// Cost Center Modal Component
const CostCenterModal: React.FC<{
  costCenter: CostCenter | null;
  plants: Plant[];
  costCenters: CostCenter[];
  onClose: () => void;
  onSubmit: (data: any) => void;
}> = ({ costCenter, plants, costCenters, onClose, onSubmit }) => {
  const { register, handleSubmit, formState: { errors } } = useForm<any>({
    defaultValues: costCenter || {
      annualBudget: 0,
      monthlyBudget: 0,
      isDirectCost: true,
      isProfitCenter: false,
      isActive: true,
      type: CostCenterType.PRODUCTION,
    },
  });

  // Filter out the current cost center from parent options to avoid circular reference
  const availableParents = costCenters.filter(cc => cc.id !== costCenter?.id);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">
          {costCenter ? 'Edit Cost Center' : 'Add New Cost Center'}
        </h2>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Code *
              </label>
              <input
                {...register('code', { required: 'Code is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              {errors.code && (
                <p className="text-red-500 text-sm mt-1">{errors.code.message as string}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <input
                {...register('name', { required: 'Name is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name.message as string}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type *
              </label>
              <select
                {...register('type', { required: 'Type is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {Object.entries(CostCenterType).map(([key, value]) => (
                  <option key={value} value={value}>
                    {key.charAt(0) + key.slice(1).toLowerCase()}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Plant *
              </label>
              <select
                {...register('plantId', { required: costCenter ? false : 'Plant is required' })}
                disabled={!!costCenter}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${costCenter ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              >
                <option value="">Select Plant</option>
                {plants.map((plant) => (
                  <option key={plant.id} value={plant.id}>
                    {plant.name} ({plant.code})
                  </option>
                ))}
              </select>
              {errors.plantId && (
                <p className="text-red-500 text-sm mt-1">{errors.plantId.message as string}</p>
              )}
              {costCenter && (
                <p className="text-gray-500 text-xs mt-1">Plant cannot be changed after creation</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Parent Cost Center
              </label>
              <select
                {...register('parentId')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">None (Top Level)</option>
                {availableParents.map((cc) => (
                  <option key={cc.id} value={cc.id}>
                    {cc.name} ({cc.code})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Manager Name
              </label>
              <input
                {...register('managerName')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Manager Email
              </label>
              <input
                type="email"
                {...register('managerEmail')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Annual Budget (₹)
              </label>
              <input
                type="number"
                step="0.01"
                {...register('annualBudget', { valueAsNumber: true })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monthly Budget (₹)
              </label>
              <input
                type="number"
                step="0.01"
                {...register('monthlyBudget', { valueAsNumber: true })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                GL Account Code
              </label>
              <input
                {...register('glAccountCode')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Analytical Account Code
              </label>
              <input
                {...register('analyticalAccountCode')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              {...register('description')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              {...register('notes')}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                {...register('isDirectCost')}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Direct Cost Center</span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                {...register('isProfitCenter')}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Profit Center</span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                {...register('isActive')}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Active</span>
            </label>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              {costCenter ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CostCenterManagement;